import { useEffect } from 'react';

/**
 * Scroll-driven behaviour for the landing page.
 *
 * `useScrollTilt` and `useScrollProgress` are gone: the parallax hero art they
 * drove no longer exists, and the header's glowing progress bar contradicted
 * the brand board's "one accent per element, never gradients".
 */

/**
 * Adds `.mn-revealed` to every `[data-reveal]` as it enters the viewport.
 *
 * Two things worth knowing before changing this.
 *
 * The query runs once, on mount. Anything mounted later is never observed and
 * stays at `opacity: 0` permanently, so every block on the page must render
 * eagerly — do not lazily mount a section.
 *
 * The stagger is indexed **per section**, not across the document. Keying it
 * on global DOM order meant inserting one target anywhere re-phased every
 * block below it, which is how a deliberate rhythm turns into noise. Three
 * steps of 20ms keeps the slowest element inside the board's 300ms budget
 * (240ms transition + 40ms delay).
 */
export function useScrollReveal(): void {
    useEffect(() => {
        const targets = document.querySelectorAll<HTMLElement>('[data-reveal]');

        if (targets.length === 0) {
            return;
        }

        if (prefersReducedMotion()) {
            targets.forEach((el) => el.classList.add('mn-revealed'));

            return;
        }

        const counters = new Map<Element, number>();

        targets.forEach((el) => {
            const section =
                el.closest('section, footer, header') ?? document.body;
            const index = counters.get(section) ?? 0;
            counters.set(section, index + 1);
            el.style.transitionDelay = `${(index % 3) * 20}ms`;
        });

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    entry.target.classList.add('mn-revealed');
                    observer.unobserve(entry.target);
                });
            },
            { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
        );

        targets.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);
}

function prefersReducedMotion(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}
